/* ─── Checko.ru CDN Data: Activity (OKVED) & Location trees ─── */

export interface ActivityNode {
  id: string
  name: string
  code: string
  children?: ActivityNode[]
}

export interface LocationNode {
  id: string
  name: string
  children?: LocationNode[]
}

export interface LocationFlat {
  id: string
  name: string
  parentId: string | null
}

export interface ActivityFlat {
  id: string
  name: string
  code: string
  parentId: string | null
}

let activityCache: ActivityNode[] | null = null
let locationCache: LocationNode[] | null = null
let activityFlatCache: ActivityFlat[] | null = null
let locationFlatCache: LocationFlat[] | null = null

function parseActivityData(raw: string): ActivityNode[] {
  try { return JSON.parse(raw) as ActivityNode[] } catch { return [] }
}

function parseLocationData(raw: string): LocationNode[] {
  try { return JSON.parse(raw) as LocationNode[] } catch { return [] }
}

function flattenActivities(nodes: ActivityNode[], parentId: string | null = null): ActivityFlat[] {
  const result: ActivityFlat[] = []
  for (const node of nodes) {
    result.push({ id: node.id, name: node.name, code: node.code, parentId })
    if (node.children?.length) result.push(...flattenActivities(node.children, node.id))
  }
  return result
}

function flattenLocations(nodes: LocationNode[], parentId: string | null = null): LocationFlat[] {
  const result: LocationFlat[] = []
  for (const node of nodes) {
    result.push({ id: node.id, name: node.name, parentId })
    if (node.children?.length) result.push(...flattenLocations(node.children, node.id))
  }
  return result
}

function extractJsAssignment(text: string): string {
  const eqIdx = text.indexOf('=')
  if (eqIdx < 0) return text
  let s = text.slice(eqIdx + 1).trim()
  if (s.startsWith("'") && s.endsWith("';")) s = s.slice(1, -2)
  else if (s.startsWith("'") && s.endsWith("'")) s = s.slice(1, -1)
  else if (s.endsWith(';')) s = s.slice(0, -1)
  return s
}

export async function fetchActivityData(): Promise<ActivityNode[]> {
  if (activityCache) return activityCache
  try {
    const resp = await fetch('https://checko.ru/cdn/activity_data.js')
    const text = await resp.text()
    const json = extractJsAssignment(text)
    activityCache = parseActivityData(json)
    activityFlatCache = null
    return activityCache ?? []
  } catch { return [] }
}

export async function fetchLocationData(): Promise<LocationNode[]> {
  if (locationCache) return locationCache
  try {
    const resp = await fetch('https://checko.ru/cdn/location_data.js')
    const text = await resp.text()
    const json = extractJsAssignment(text)
    locationCache = parseLocationData(json)
    locationFlatCache = null
    return locationCache ?? []
  } catch { return [] }
}

export async function getFlatActivities(): Promise<ActivityFlat[]> {
  if (activityFlatCache) return activityFlatCache
  const tree = await fetchActivityData()
  activityFlatCache = flattenActivities(tree)
  return activityFlatCache
}

export async function getFlatLocations(): Promise<LocationFlat[]> {
  if (locationFlatCache) return locationFlatCache
  const tree = await fetchLocationData()
  locationFlatCache = flattenLocations(tree)
  return locationFlatCache
}

export async function searchActivities(query: string, limit = 50): Promise<ActivityFlat[]> {
  const flat = await getFlatActivities()
  if (!query) return flat.slice(0, limit)
  const q = query.toLowerCase()
  return flat.filter(n => n.name.toLowerCase().includes(q) || n.code.toLowerCase().includes(q)).slice(0, limit)
}

export async function searchLocations(query: string, limit = 50): Promise<LocationFlat[]> {
  const flat = await getFlatLocations()
  if (!query) return flat.slice(0, limit)
  const q = query.toLowerCase()
  return flat.filter(n => n.name.toLowerCase().includes(q)).slice(0, limit)
}
